import {
  computed,
  reactive,
  ref,
  watch,
  Ref,
} from '@vue/composition-api';
import { useQuery, useResult } from '@vue/apollo-composable';
import _ from 'lodash';

import { PaginatorInfo } from '@/types/paginatorInfo';

import getVehicles from '@/gql/vehicle/getLeasedVehicles.gql';
import getDeliveries from '@/gql/delivery/getDeliveries.gql';
import getDrivers from '@/gql/driver/getDrivers.gql';
import getMostStableTotalItem from '@/gql/deliveryNote/getMostStableTotalItem.gql';
import countResendDelivery from '@/gql/delivery/countResendDelivery.gql';

import useAuth from '@/useAuth';
import useDelivery from '@/useDelivery';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useDeliveryNoteStatus from '@/useDeliveryNote';

const { sumPackagesPrice } = useDelivery();
const { translateDistanceType, farthestDeliveryDistanceIndex } = useDeliveryAreaCode();
const { deliveriesStatusToDeliveryNoteStatus } = useDeliveryNoteStatus();

const filterVehicleDropdown = ['เลขทะเบียนรถ', 'ประเภทของรถ'];
const filterVehicleValues = ['license', 'model'];

const search = reactive({
  vehicleName: '',
  vehicleSearchInput: '',
  pendingVehicleSearch: false,
  deliveryName: '',
  deliverySearchInput: '',
  deliveryArea: '',
  deliveryAreaInput: null,
  deliverySort: [{ column: 'updatedAt', order: 'DESC' }],
  deliverySortInput: 'updatedAt',
  vehicleSort: [{ column: 'license', order: 'DESC' }],
  vehicleSortInput: 'license',
  pendingDeliverySearch: false,
});
const deliveryNameSearchHandler = _.debounce(() => {
  search.pendingDeliverySearch = true;
  search.deliveryName = search.deliverySearchInput;
}, 500);
const deliveryFilterHandler = _.debounce(() => {
  search.pendingDeliverySearch = true;
  search.deliveryArea = (search.deliveryAreaInput as any)?.code;
}, 100);
const sortDeliveryDisplay = ['วันที่แก้ไขล่าสุด', 'พื้นที่จัดส่ง', 'วันที่รายการถูกสร้าง', 'เลขที่บิล', 'วันที่สั่งซื้อ', 'ระดับความเร่งด่วน', 'วันนัดส่ง', 'รหัสการจัดส่ง'];
const sortDeliveryList = ['updatedAt', 'area_id', 'createdAt', 'orderNumber', 'orderDate', 'urgentLevel', 'targetDate', 'deliveryCode'];
const deliverySortHandler = _.debounce(() => {
  search.pendingDeliverySearch = true;
  search.deliverySort = [{ column: search.deliverySortInput, order: 'DESC' }];
});
const vehicleNameSearchHandler = _.debounce(() => {
  search.pendingVehicleSearch = true;
  search.vehicleName = search.vehicleSearchInput;
});
const vehicleSortHandler = _.debounce(() => {
  search.pendingVehicleSearch = true;
  search.vehicleSort = [{ column: search.vehicleSortInput, order: 'DESC' }];
});

const vehicles: Ref<any> = ref([]);
const vehiclePaginatorInfo: Ref<PaginatorInfo> = ref({
  currentPage: 1,
  perPage: 30,
});
const vehiclesPage = ref(1);
const vehicleStaticTotal = ref(1);
const getVehicleFirstTime = ref(true);

const deliveries: Ref<any> = ref([]);
const deliveryPaginatorInfo: Ref<PaginatorInfo> = ref({
  currentPage: 1,
  perPage: 30,
});
const deliveriesPage = ref(1);
const deliveryStaticTotal = ref(1);
const getDeliveryFirstTime = ref(true);

const drivers = ref([]);
const driverPaginatorInfo = ref({});

export default function createDeliveryNote(date = { value: null }) {
  const { me } = useAuth();

  const { result: getMostStableTotalItemResult } = useQuery(getMostStableTotalItem, computed(() => ({
    // filterFreeDate: date !== null ? date.value : undefined,
    filterHubId: me.hub?.id,
    // filterDestinationHubId: destinationHubId || undefined,
    filterByStatus: 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
  })), () => ({ fetchPolicy: 'network-only' }));
  const mostStableTotalItem = useResult(getMostStableTotalItemResult, {
    deliveries: { paginatorInfo: { total: 0 } },
    vehicles: { paginatorInfo: { total: 0 } },
  }, (data) => data);
  /**
   *
   * === Vehicle ===
   *
   */
  const { result: getVehiclesResult, loading: vehiclesLoading } = useQuery(getVehicles, computed(() => ({
    page: vehiclesPage.value,
    orderBy: search.vehicleSortInput !== '' ? search.vehicleSort : undefined,
    filter: search.vehicleName !== '' ? {
      OR: [
        { column: 'MODEL', operator: 'LIKE', value: `%${search.vehicleName}%` },
        { column: 'LICENSE', operator: 'LIKE', value: `%${search.vehicleName}%` },
      ],
    } : undefined,
    filterByLeasedDelivery: true,
    // filterFreeDate: date !== null ? date.value : undefined,
  })), () => ({ fetchPolicy: 'network-only' }));
  const vehiclesResult = useResult(getVehiclesResult, [], (data) => data.vehicles);
  watch(() => vehiclesResult.value, (newVal, oldVal) => {
    if (!vehiclesLoading.value && newVal !== oldVal) {
      const vehicleList = vehiclesResult.value.data.map((vehicle: any) => ({
        ...vehicle,
        driver: vehicle.defaultDriver || { id: -1 },
      }));
      if (search.pendingVehicleSearch || vehiclesResult.value.paginatorInfo.currentPage === vehiclePaginatorInfo.value.currentPage) {
        vehicles.value = [];
        vehicles.value = vehicleList;
        vehiclesPage.value = 1;
        search.pendingVehicleSearch = false;
      } else vehicles.value = [...vehicles.value, ...vehicleList];
      vehiclePaginatorInfo.value = vehiclesResult.value.paginatorInfo;
      if (getVehicleFirstTime.value) {
        vehicleStaticTotal.value = vehiclesResult.value.paginatorInfo;
      }
    }
  });

  /**
   *
   * === Delivery ===
   *
   */
  const { result: getDeliveriesResult, loading: deliveriesLoading } = useQuery(getDeliveries, computed(() => {
    let page = deliveriesPage.value;
    if (search.pendingDeliverySearch) page = 1;
    return {
      page,
      filter: {
        column: 'DELIVERY_NOTE_ID',
        operator: 'IS_NULL',
        OR: search.deliveryName !== '' ? [
          { column: 'CONTACT_NAME', operator: 'LIKE', value: `%${search.deliveryName}%` },
          { column: 'ORDER_NUMBER', operator: 'LIKE', value: `%${search.deliveryName}%` },
          { column: 'CUSTOMER_NAME', operator: 'LIKE', value: `%${search.deliveryName}%` },
        ] : undefined,
      },
      filterArea: search.deliveryArea ? {
        OR: [
          { column: 'CODE', operator: 'LIKE', value: `%${search.deliveryArea}%` },
          { column: 'NAME', operator: 'LIKE', value: `%${search.deliveryArea}%` },
        ],
      } : undefined,
      orderBy: search.deliverySortInput !== '' ? search.deliverySort : undefined,
      filterExcludeStatus: ['PENDING_ORDER_CONFIRMATION'],
      // filterHubId: me.hubId,
      // filterDeliveryNoteId: null,
      // searchCustomerName: search.deliveryName !== '' ? `%${search.deliveryName}%` : undefined,
    };
  }), () => ({ fetchPolicy: 'network-only' }));
  const deliveriesResult = useResult(getDeliveriesResult, [], (data) => data.deliveries);
  watch(() => getDeliveriesResult.value, (newVal, oldVal) => {
    if (!deliveriesLoading.value && newVal !== oldVal) {
      const mappedDelivery = deliveriesResult.value.data.map((delivery: any) => ({
        ...delivery,
        packagesPrice: sumPackagesPrice(delivery.packages),
      }));

      if (search.pendingDeliverySearch || deliveriesResult.value.paginatorInfo.currentPage === deliveryPaginatorInfo.value.currentPage) {
        deliveries.value = [];
        deliveries.value = mappedDelivery;
        deliveriesPage.value = 1;
        search.pendingDeliverySearch = false;
      } else deliveries.value = [...deliveries.value, ...mappedDelivery];
      deliveryPaginatorInfo.value = deliveriesResult.value.paginatorInfo;
      if (getDeliveryFirstTime.value) {
        deliveryStaticTotal.value = vehiclesResult.value.paginatorInfo;
      }
    }
  });
  watch(() => deliveries.value.length, (newVal: number, oldVal: number) => {
    if (newVal !== oldVal) {
      if (deliveries.value.length === 0 && deliveryPaginatorInfo.value.hasMorePages) {
        deliveriesPage.value += 1;
      }
    }
  });

  const { result: getDriversResult, loading: driversLoading } = useQuery(getDrivers);
  const driversResult = useResult(getDriversResult, [], (data) => data.drivers);
  watch(() => driversResult.value, (newVal, oldVal) => {
    if (!driversLoading.value && newVal !== oldVal) {
      drivers.value = driversResult.value.data;
      driverPaginatorInfo.value = driversResult.value.paginatorInfo;
    }
  });

  const loading = computed(() => vehiclesLoading.value || deliveriesLoading.value || driversLoading.value);

  const { result: getCountResendResult } = useQuery(countResendDelivery, (() => ({
    filterHubId: me.hubId,
    // filterDestinationHubId: destinationHubId || undefined,
  })));
  const countResend = useResult(getCountResendResult, 0, (data: any) => data.totalResendDelivery.paginatorInfo.total);

  return {
    deliverySortHandler,
    deliveryNameSearchHandler,
    deliveryFilterHandler,
    sortDeliveryDisplay,
    sortDeliveryList,

    search,

    filterVehicleDropdown,
    filterVehicleValues,
    vehicleNameSearchHandler,
    vehicleSortHandler,
    vehicles,
    vehiclePaginatorInfo,
    vehiclesPage,
    vehiclesResult,
    vehiclesLoading,
    vehicleStaticTotal,

    deliveries,
    deliveryPaginatorInfo,
    deliveriesPage,
    deliveriesResult,
    deliveriesLoading,
    deliveryStaticTotal,
    countResend,

    drivers,
    driverPaginatorInfo,
    driversResult,
    driversLoading,

    loading,

    mostStableTotalItem,
  };
}

export function getDeliveryNoteInformation(deliveriesArgs: any) {
  let totalArrangeDelivery = 0;
  let weight = 0;
  let price = 0;
  let packagesPrice = 0;
  let volume = 0;
  const distanceArr: string[] = [];
  const pickupLeasedFee: number[] = [];
  const sixWheelLeasedFee: number[] = [];
  const tenWheelLeasedFee: number[] = [];
  const trailerLeasedFee: number[] = [];
  const deliveryAreaCodeArr: string[] = [];
  totalArrangeDelivery += deliveriesArgs.length;
  deliveriesArgs.forEach((delivery: any) => {
    weight += delivery.totalWeight;
    price += delivery.deliveryFee;
    packagesPrice += sumPackagesPrice(delivery.packages);
    distanceArr.push(delivery.area?.distanceType || '');
    pickupLeasedFee.push(delivery.area?.pickupLeasedCost || 0);
    sixWheelLeasedFee.push(delivery.area?.sixWheelLeasedCost || 0);
    tenWheelLeasedFee.push(delivery.area?.tenWheelLeasedCost || 0);
    trailerLeasedFee.push(delivery.area?.trailerLeasedCost || 0);
    deliveryAreaCodeArr.push(`${delivery.area?.code || ''} ${translateDistanceType(delivery.area?.distanceType)}`);
    delivery.packages.forEach((pack: any) => {
      volume += pack.volume || 0;
    });
  });
  const distanceIndex = farthestDeliveryDistanceIndex(distanceArr);
  return {
    totalArrangeDelivery,
    weight,
    price,
    volume,
    distance: distanceArr[distanceIndex as number],
    pickupLeasedFee: pickupLeasedFee[distanceIndex as number],
    sixWheelLeasedFee: sixWheelLeasedFee[distanceIndex as number],
    tenWheelLeasedFee: tenWheelLeasedFee[distanceIndex as number],
    trailerLeasedFee: trailerLeasedFee[distanceIndex as number],
    deliveryAreaCode: deliveryAreaCodeArr[distanceIndex as number],
    packagesPrice,
    area: deliveriesArgs[distanceIndex as number]?.area,
    status: deliveriesStatusToDeliveryNoteStatus(deliveriesArgs),
  };
}
